import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import usePauseControl from '../../hooks/usePauseControl';
import { useSelector } from 'react-redux';
import { selectReaderManager } from '../../../ReaderManagerEvents/ReaderManagerSlice';
import useEndingSpeech from '../../hooks/useEndingSpeech';
import useDidAttempt from '../../hooks/useDidAttempt';
import usePhraseTypeIndexes from '../../hooks/usePhraseTypeIndexes';
import { phraseTypes } from '../../../../../constants/constants';
import { COPY_STRINGS } from '../../../../../constants/translatedCopy';
import { UpAndDown } from 'amira-intervention-library';
import useFetchWordMetadata from "../../hooks/useFetchWordMetadata";

export default function UpAndDownAssessment(props) {

  const {
    metadata,
    currentPageIndex,
    paused,
    setWillHandlePause,
    startPhrase,
    pausePhrase,
    resumePhrase,
    doneHandlingPause,
    transcript,
    setDisableInterventionsForPhrase
  } = props;

  const { currentWordIndex, phraseMetadata } = metadata;

  const { shouldHandlePause, finishHandlePause, passPauseControlBackToReaderManager, takeOverPauseControl } = usePauseControl({
    paused,
    setWillHandlePause,
    pausePhrase,
    resumePhrase,
    doneHandlingPause
  });

  const readerManagerStates = useSelector(selectReaderManager);
  const { contextStates: { minTimeElapsed } } = readerManagerStates;

  const { addToEndingSequence, hasPendingEndingSequence } = useEndingSpeech();

  const { didAttempt, clearDidAttempt } = useDidAttempt({ transcript })

  const {
    isExamplePhraseOfThisType,
    isLastPhraseOfThisType,
    currentPhraseIndexOfThisType,
    isLastPageOfStory,
  } = usePhraseTypeIndexes(currentPageIndex, phraseTypes.UP_AND_DOWN);

  useEffect(() => {
    setDisableInterventionsForPhrase(true)
    clearDidAttempt();
}, []);

  /**
   * assessment handle when there is an attempt by the user
   */
  useEffect(() => {
    if (didAttempt) {
      // registerEndingSpeech();
      passPauseControlBackToReaderManager();
    }
  }, [didAttempt]);

  useEffect(() => {
    // for multi-word phrases, we need to clear the didAttempt state when moving to the next word so it can track the attempt for the new word.
    clearDidAttempt();
    // take over pause control after wordIndex is updated
    takeOverPauseControl();
  }, [currentWordIndex]);

  const { instructions } = phraseMetadata;
  const { wordMetadata: exampleWordMetadata, loading: exampleLoading } = useFetchWordMetadata({ word: instructions?.text })
  
  const example = instructions && instructions.text && exampleWordMetadata ? {
    word: instructions.text,
    GRAPHEMIC_BREAKDOWN: exampleWordMetadata.GRAPHEMIC_BREAKDOWN,
    PHON: exampleWordMetadata.PHON,
  }: null

  if(exampleLoading) {
    return null;
  }

  /**
     * assessment register an ending speech for reader manager to play on end of phrase
     */
  const registerEndingSpeech = () => {
    if (isExamplePhraseOfThisType) {
      addToEndingSequence({ textToSpeak: COPY_STRINGS.intervention_strings.change_one_part.assessment_handle_item_done_try_another });
    } else if (isLastPhraseOfThisType) {
      if (isLastPageOfStory) {
        addToEndingSequence({ textToSpeak: COPY_STRINGS.intervention_strings.change_one_part.end });
      } else {
        addToEndingSequence({ textToSpeak: COPY_STRINGS.intervention_strings.change_one_part.end_with_more_activities });
      }
    };
  };

  // add anything that is specific to assessment mode
  const assessmentMetadata = {
    version: 4,
    ...metadata,
    example,
    handleMessage: (message) => { // basically gives it the full control of the phrase timing
      const { event } = message;
      if (event === "start") {
        // after intro
        startPhrase();
      } else if (event === "retry") {
        // clear the phrase timeout so that we can speak without the clock ticking and move to the next phrase during the speech
        finishHandlePause();
        passPauseControlBackToReaderManager();
      } else if (event === "end") {
        finishHandlePause();
        passPauseControlBackToReaderManager();
      }
    }
  };

  const states = {
    paused: paused && shouldHandlePause,
    didAttempt: didAttempt && minTimeElapsed
  };

  return (
    <UpAndDown {...assessmentMetadata} {...states} />
  );
}

UpAndDownAssessment.propTypes = {
  metadata: PropTypes.object.isRequired,
  currentPageIndex: PropTypes.number.isRequired,
  paused: PropTypes.bool.isRequired,
  setWillHandlePause: PropTypes.func.isRequired,
  startPhrase: PropTypes.func.isRequired,
  pausePhrase: PropTypes.func.isRequired,
  resumePhrase: PropTypes.func.isRequired,
  doneHandlingPause: PropTypes.func.isRequired,
  transcript: PropTypes.object.isRequired,
}